import { useState, useCallback, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Download, Loader2, RefreshCw, Trash2 } from 'lucide-react';
import { GlassPanel } from '@/components/layout';
import { cn } from '@/lib/cn';
import { staggerContainer, staggerItem } from '@/lib/motion';
import { documentsApi, imageApi, type GeneratedDocumentItem, type GeneratedImage } from '@/lib/api';
import { DocumentCard } from '@/components/voice/DocumentCard';

type DownloadItem =
  | { type: 'document'; item: GeneratedDocumentItem; sortKey: string }
  | { type: 'image'; item: GeneratedImage; sortKey: string };

/**
 * Downloads Tab - All voice-generated files in one place
 *
 * Shows documents (PDF, PPTX, etc.) and images generated during voice sessions.
 * No standalone image generation form; everything comes from the voice agent.
 */
export function DownloadsTab() {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState<DownloadItem[]>([]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [docRes, imgRes] = await Promise.all([
        documentsApi.history(0, 50),
        imageApi.history(0, 50).catch(() => ({ data: [] as GeneratedImage[] })),
      ]);
      const docs: DownloadItem[] = (docRes.data || []).map((d) => ({
        type: 'document' as const,
        item: d,
        sortKey: d.created_at || d.id,
      }));
      const imgs: DownloadItem[] = (imgRes.data || []).map((i) => ({
        type: 'image' as const,
        item: i,
        sortKey: i.created_at || i.id,
      }));
      const merged = [...docs, ...imgs].sort(
        (a, b) => new Date(b.sortKey).getTime() - new Date(a.sortKey).getTime()
      );
      setItems(merged);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  // BUG-026: Auto-refresh when new items are generated during voice session
  useEffect(() => {
    const handleRefresh = () => fetchAll();
    window.addEventListener('dryvox:downloads-refresh', handleRefresh);
    return () => window.removeEventListener('dryvox:downloads-refresh', handleRefresh);
  }, [fetchAll]);

  const handleDeleteImage = useCallback(
    async (id: string) => {
      try {
        await imageApi.delete(id);
        setItems((prev) => prev.filter((i) => !(i.type === 'image' && i.item.id === id)));
      } catch (e) {
        console.error('Delete image failed:', e);
      }
    },
    []
  );

  const handleDownloadImage = useCallback((img: GeneratedImage) => {
    const link = document.createElement('a');
    link.href = `data:${img.mime_type};base64,${img.image_base64}`;
    link.download = `dryvox-${img.id}.${img.mime_type?.split('/')[1] || 'jpg'}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, []);

  const handleDeleteDocument = useCallback(() => {
    fetchAll();
  }, [fetchAll]);

  return (
    <motion.div
      variants={staggerContainer}
      initial="hidden"
      animate="visible"
      className="space-y-6"
    >
      <motion.div variants={staggerItem}>
        <GlassPanel className="p-4">
          <div className="flex justify-between items-center mb-4 px-2">
            <h3 className="text-sm font-medium text-white/80">
              Generated by voice
            </h3>
            {!loading && (
              <button
                onClick={fetchAll}
                className="flex items-center gap-1.5 text-xs text-cyan-400 hover:text-cyan-300"
              >
                <RefreshCw className="w-3.5 h-3.5" />
                Refresh
              </button>
            )}
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-white/40" />
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-12">
              <Download className="w-12 h-12 text-white/20 mx-auto mb-3" />
              <p className="text-sm text-white/50 max-w-sm mx-auto">
                Files generated during voice sessions will appear here. Ask the assistant to create a report, presentation, or image.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {items.map((entry) =>
                entry.type === 'document' ? (
                  <DocumentCard
                    key={`doc-${entry.item.id}`}
                    id={entry.item.id}
                    title={entry.item.title}
                    type={entry.item.document_type as 'pdf' | 'pptx' | 'html' | 'docx'}
                    filename={entry.item.filename}
                    createdAt={entry.item.created_at ?? undefined}
                    onDelete={handleDeleteDocument}
                    showDelete={true}
                  />
                ) : (
                  <motion.div
                    key={`img-${entry.item.id}`}
                    variants={staggerItem}
                    className={cn(
                      'flex items-center gap-3 p-4 rounded-xl bg-white/5 border border-white/10'
                    )}
                  >
                    <div className="flex-shrink-0 w-14 h-14 rounded-lg overflow-hidden bg-white/10">
                      <img
                        src={`data:${entry.item.mime_type};base64,${entry.item.image_base64}`}
                        alt={entry.item.prompt}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-white truncate">
                        {entry.item.prompt?.slice(0, 50) || 'Generated image'}
                        {(entry.item.prompt?.length ?? 0) > 50 ? '…' : ''}
                      </p>
                      <p className="text-xs text-white/50">
                        Image • {entry.item.created_at ? new Date(entry.item.created_at).toLocaleDateString() : ''}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => handleDownloadImage(entry.item)}
                        className="p-2 rounded-lg bg-cyan-500/20 text-cyan-300 hover:bg-cyan-500/30"
                        title="Download"
                      >
                        <Download className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDeleteImage(entry.item.id)}
                        className="p-2 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </motion.div>
                )
              )}
            </div>
          )}
        </GlassPanel>
      </motion.div>
    </motion.div>
  );
}

export default DownloadsTab;
